import React, { Fragment } from "react";
import { cls } from "cls";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import Stars from "~/common/Stars";
import { Product } from "~/common/Store/models/ProductListing";
import Icon from "~/common/Icons/Standard";
import { PlusLink } from "@clarkinc/zest-design-system/ui";

interface Props {
  mostRecentOutletProducts: Product[];
}

const OutletNewProductsCarousel = ({ mostRecentOutletProducts }: Props) => {
  const carouselBox = (product: Product) => (
    <li
      className="ag-item gtm-product gtm-product-ajax"
      style={{ maxHeight: "none", height: "auto" }}
      data-testid="outletNewProduct"
    >
      <div className="flex flex-col h-full">
        <div className="product-image mb-3">
          <a href={product.link} className="image" title={product.description}>
            <img src={product.primaryImagePath} alt={product.description} />
          </a>
        </div>
        <div className="details mt-auto justify-self-center">
          <div className="flex items-center justify-center mb-2">
            {product.isWebstaurantPlusEligible && (
              <PlusLink className="mr-0.5" data-testid={`plusLink`} />
            )}
            <Stars rating={product.overallRating} />
          </div>
          <a
            href={product.link}
            className="description"
            data-testid="itemDescription"
          >
            {product.descriptionHtml}
          </a>
          {product.outletAttributes.condition && (
            <span
              data-testid="outletCondition"
              data-component="outlet-condition"
              className="inline-block bg-gray-100 rounded-full px-3 py-1 text-center font-semibold text-xs text-gray-800 my-1 leading-snug self-center"
            >
              {product.outletAttributes.condition}
            </span>
          )}
        </div>
        <div
          className="pricing mb-0 mt-auto justify-self-end"
          data-component="Pricing"
        >
          <p className="was-price text-center">
            {`New: ${formatAsCurrency.formatIfUndefined(
              product.outletAttributes.originalPrice,
            )}`}
          </p>
          <p className="price">
            <span className="sr-only">Outlet Price: </span>
            {`${formatAsCurrency.formatIfUndefined(product.price.price)}`}
            <span className="each">/Each</span>
          </p>
        </div>
      </div>
    </li>
  );

  return (
    <div
      data-component="outlet-new-products"
      className="flex flex-col md:flex-row md:bg-gray-50 w-full box-border md:-mt-10 mb-3 md:mb-5 md:p-5 md:border md:border-solid md:border-gray-200 rounded"
    >
      <div className="md:w-2/5 flex mb-4 md:mb-0 items-center justify-between">
        <div className="flex md:flex-col justify-between items-center md:items-start w-full md:pl-6 lg:w-3/5">
          <p className="text-2xl md:text-5xl mb-0 font-semibold md:max-w-xs leading-none">
            New Discounts{" "}
            <span className="block md:mt-2 text-lg font-normal">
              Deals added regularly
            </span>
          </p>
          <a
            href="/outlet.html?order=date_desc"
            title=""
            className="btn btn-info md:mt-4 py-2 md:py3 md:w-20"
            aria-label="Shop New Outlet Products"
          >
            Shop All
          </a>
        </div>
        <div className="hidden lg:flex w-2/5 relative h-full">
          <img
            src="//cdnimg.webstaurantstore.com/uploads/design/2021/1/outlet-image.png"
            className="absolute right-0 top-0 bottom-0 my-auto max-h-full -mr-2"
            alt="Save big on discounted appliances"
          />
        </div>
      </div>
      <div className="md:w-3/5">
        <div id="recommended" className="carousel-wrap">
          <div
            className="basic-carousel carousel-box init"
            data-component="carousel"
          >
            <div className="ag-carousel-nav-svg">
              <button
                type="button"
                className="ag-car-prev"
                data-control="prev"
                aria-label="previous set of slides"
              >
                <Icon name="left-open" height="1.25rem" width="1.25rem" />
              </button>
            </div>
            <div id="otherProductsCarousel" className="ag-carousel-frame box">
              <ul
                className="ag-carousel ag-full"
                style={{ maxHeight: "none", height: "auto" }}
                data-testid="outletNewProductsCarousel"
              >
                {mostRecentOutletProducts.map((product) => (
                  <Fragment key={product.itemNumber}>
                    {carouselBox(product)}
                  </Fragment>
                ))}
              </ul>
            </div>
            <div className="ag-carousel-nav-svg">
              <button
                type="button"
                className="ag-car-next"
                data-control="next"
                aria-label="next set of slides"
              >
                <Icon name="right-open" height="1.25rem" width="1.25rem" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default OutletNewProductsCarousel;
